import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api';
import { safeFormatDate } from '../../../lib/format';
import { useAuthStore } from '../../../store/auth.store';

export interface OperationKeyState {
  /** The key and the fields it was created for. */
  key: string;
  fields: string;
  /** The last submission failed without a clear answer (no response, timeout or 5xx). */
  uncertain: boolean;
  /** Counts history reloads; only the latest one may clear `uncertain`. */
  generation: number;
}

export type OperationKeyEvent =
  | { type: 'fields'; fields: string }
  | { type: 'success' }
  | { type: 'failure'; uncertain: boolean }
  | { type: 'reloaded'; generation: number; fields: string };

/**
 * One operation key per intended submission (2L "Double-post protection"): a confirmed success
 * or a change to any field takes a new key; a retry after an uncertain failure resends the same
 * key with the unchanged fields, so the server answers it as a replay. After an uncertain failure
 * the key stays until a history reload succeeds (`reload` asks for one, numbered by
 * `generation`); only then may changed fields take a new key, so staff first see whether the
 * earlier attempt went through. A failed or outdated reload changes nothing. Forms lock their
 * fields while a request is pending, so the key and fields cannot change before its answer.
 */
export function nextOperationKey(
  state: OperationKeyState,
  event: OperationKeyEvent,
  newKey: () => string,
): { state: OperationKeyState; reload: boolean } {
  const reload = (next: OperationKeyState) => ({
    state: { ...next, generation: next.generation + 1 },
    reload: true,
  });
  switch (event.type) {
    case 'success':
      return { state: { ...state, key: newKey(), uncertain: false }, reload: false };
    case 'failure':
      return event.uncertain
        ? reload({ ...state, uncertain: true })
        : { state: { ...state, uncertain: false }, reload: false };
    case 'fields':
      if (event.fields === state.fields) return { state, reload: false };
      if (state.uncertain) return reload(state);
      return { state: { ...state, key: newKey(), fields: event.fields }, reload: false };
    case 'reloaded':
      if (event.generation !== state.generation || !state.uncertain)
        return { state, reload: false };
      return {
        state:
          event.fields === state.fields
            ? { ...state, uncertain: false }
            : { ...state, key: newKey(), fields: event.fields, uncertain: false },
        reload: false,
      };
  }
}

/** Submitting is safe unless an uncertain attempt is unresolved and the fields changed. */
export const operationKeyReady = (state: OperationKeyState, fields: string) =>
  !state.uncertain || fields === state.fields;

/** No response, a timeout or a server error: the request may have been applied. */
export function isUncertainFailure(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === undefined || status >= 500;
}

/**
 * `reload` must reject when the history could not be read (for example
 * `queryClient.refetchQueries(..., { throwOnError: true })`); the form then stays blocked.
 */
export function useOperationKey(fields: unknown, reload: () => Promise<unknown>) {
  const serialized = JSON.stringify(fields);
  const [state, setState] = useState<OperationKeyState>(() => ({
    key: crypto.randomUUID(),
    fields: serialized,
    uncertain: false,
    generation: 0,
  }));
  const latest = useRef({ state, serialized, reload });
  latest.current = { state, serialized, reload };
  const apply = (event: OperationKeyEvent) => {
    const next = nextOperationKey(latest.current.state, event, () => crypto.randomUUID());
    latest.current.state = next.state;
    setState(next.state);
    if (!next.reload) return;
    const generation = next.state.generation;
    latest.current
      .reload()
      .then(() => apply({ type: 'reloaded', generation, fields: latest.current.serialized }))
      // A failed reload leaves the form blocked; the next edit asks for another reload.
      .catch(() => undefined);
  };
  useEffect(() => {
    apply({ type: 'fields', fields: serialized });
    // apply reads the latest state through the ref; only a field change should trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
  return {
    key: state.key,
    /** False while an uncertain attempt is unresolved and the fields changed: keep submit disabled. */
    ready: operationKeyReady(state, serialized),
    renew: () => apply({ type: 'success' }),
    failed: (error: unknown) => apply({ type: 'failure', uncertain: isUncertainFailure(error) }),
  };
}

/** The stored key of an intended reversal, created on first use. */
export function reversalKey(keys: Map<string, string>, entryId: string, note: string) {
  const id = `${entryId}|${note}`;
  const existing = keys.get(id);
  if (existing) return existing;
  const key = crypto.randomUUID();
  keys.set(id, key);
  return key;
}

export function useWalletPermissions() {
  const actions = useAuthStore((s) => s.allowedActions);
  return {
    canAdjust: !!actions?.includes('member:adjust_points'),
    canManage: !!actions?.includes('member:wallet_manage'),
  };
}

interface TournamentOption {
  id: string;
  name: string;
  buyin: string;
  startTime?: string | null;
  createdAt: string;
}

/** Recent tournaments of the session venue (the API filters by venue). */
export function TournamentSelect({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (id: string) => void;
  id?: string;
}) {
  const { t } = useTranslation('member');
  const { data } = useQuery<{ data: TournamentOption[] }>({
    queryKey: ['wallet-tournament-options'],
    queryFn: () => api.get('/tournament', { params: { limit: 100 } }),
    staleTime: 60_000,
  });
  return (
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={INPUT}
    >
      <option value="">{t('walletPickTournament')}</option>
      {data?.data.map((tournament) => (
        <option key={tournament.id} value={tournament.id}>
          {tournament.name} · {Number(tournament.buyin).toLocaleString()} ·{' '}
          {safeFormatDate(tournament.startTime ?? tournament.createdAt, (d) =>
            d.toLocaleDateString(),
          )}
        </option>
      ))}
    </select>
  );
}

export const INPUT =
  'w-full px-3 py-2 rounded-md border border-base-300 bg-base-100 min-h-11 text-sm';
export const BUTTON =
  'min-h-11 px-4 py-2 rounded-md bg-primary text-primary-content text-sm disabled:opacity-50';
export const SMALL_BUTTON =
  'min-h-11 px-3 rounded-md border border-base-300 bg-base-100 text-sm whitespace-nowrap hover:border-primary';
export const PANEL = 'bg-base-200 border border-base-300 rounded-lg p-4 space-y-3';

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="text-base-content/70 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

export function FormError({ message }: { message: string }) {
  return message ? (
    <p role="alert" className="text-sm text-error">
      {message}
    </p>
  ) : null;
}

export const formatDay = (value: string | null | undefined) =>
  value ? safeFormatDate(value, (d) => d.toLocaleDateString()) : '-';
